/**
 * Which board features have landed with the team, and which have not — least adopted first.
 *
 * ── The question this answers, which no other panel does ─────────────────────────────────────
 * The page tree says how often a feature fired. That is volume, and volume hides the thing worth
 * knowing: a feature used 400 times can still be one recruiter's habit, and a feature used twelve
 * times by seven people has landed. So the number that leads here is **how many boards used it at
 * all**, and the list is ordered by that ascending — everything at the top is something the team
 * has not picked up yet.
 *
 * Aggregate on purpose. The per-board panel beside this answers "who", where naming is scoped and
 * justified; this one is about the feature, and a name would add nothing to it.
 */
import { Tooltip, TooltipContent, TooltipTrigger } from '@finn-auto/design-system/atoms/tooltip';
import { formatCompact } from './chartTokens';
import { adoptionAcrossBoards, describeFeature } from './boardFeatures';
import type { BoardUsageRow } from '../../services/usageApi';

export function FeatureAdoption({ recruiters }: { recruiters: BoardUsageRow[] }) {
  const tabs = adoptionAcrossBoards(recruiters);
  const boards = recruiters.length;
  const untouched = tabs.flatMap((tab) => tab.features.filter((f) => f.boards === 0));

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-zinc-900">Which features have landed</h3>
        <p className="mt-0.5 max-w-[80ch] text-xs text-zinc-500">
          Every feature the boards offer, least adopted first, counted by how many of the{' '}
          {boards} boards use it — not by how often it fires. A feature one person uses constantly
          still has not landed, and that is the difference this ranking exists to show.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {tabs.map((tab) => (
          <div key={tab.tab}>
            <p className="mb-1.5 text-xs font-semibold text-zinc-900">{tab.label}</p>
            <ul className="divide-y divide-zinc-100">
              {tab.features.map((feature) => (
                <li key={feature.label} className="flex items-baseline justify-between gap-3 py-1.5">
                  <FeatureLabel label={feature.label} used={feature.boards > 0} />
                  <span
                    className="shrink-0 text-xs whitespace-nowrap"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {feature.boards === 0 ? (
                      <span className="text-zinc-400">no board yet</span>
                    ) : (
                      <>
                        <span className="font-medium text-zinc-900">
                          {feature.boards}/{boards}
                        </span>
                        <span className="text-zinc-400"> boards · {formatCompact(feature.uses)}</span>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {untouched.length > 0 && (
        <p className="text-xs text-zinc-500">
          {untouched.length === 1
            ? 'One feature was not used on a single board in this range'
            : `${untouched.length} features were not used on a single board in this range`} —
          which is a question about whether the team knows they exist before it is a question about
          whether they are worth keeping.
        </p>
      )}
    </section>
  );
}

function FeatureLabel({ label, used }: { label: string; used: boolean }) {
  const description = describeFeature(label);
  const text = (
    <span className={`text-xs ${used ? 'text-zinc-700' : 'text-zinc-500'}`}>{label}</span>
  );
  if (!description) return text;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="underline decoration-dotted underline-offset-2">{text}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{description}</TooltipContent>
    </Tooltip>
  );
}
